<script setup lang="ts">

import AudioController from "@/components/UI/AudioPlayer/AudioController.vue";
import Icon from "@/components/UI/Icon.vue";
import BackgroundAudio, {PlayerMetadata} from "@/ts/Audio/BackgroundAudio";
import {computed, onMounted, onUnmounted, ref, watch} from "vue";

const props = withDefaults(defineProps<PlayerMetadata & {
  theme?: 'light' | 'dark'
}>(), {
  theme: 'light'
});
const backgroundAudio = BackgroundAudio.getInstance();
const {isPlaying, playerMetadata} = backgroundAudio;
const hasNext = ref(false);
const hasPrevious = ref(false);

watch(playerMetadata, () => {
  hasNext.value = backgroundAudio.hasNext();
  hasPrevious.value = backgroundAudio.hasPrevious();
}, { immediate: true });
const onClick = () => {
  isPlaying.value ? backgroundAudio.pause() : backgroundAudio.play();
}
const onEnded = () => {
  backgroundAudio.pause()
}
onMounted(() => {
  backgroundAudio.addEventListener('ended', onEnded);
});
onUnmounted(() => {
  backgroundAudio.removeEventListener('ended', onEnded);
});
const buttonColor = computed(() => props.theme === 'light' ? '#6C7BB2' : '#FFFFFF');
const onPrev = () => {
  if (!backgroundAudio.hasPrevious()) return
  backgroundAudio.playPrevious();
}
const onNext = () => {
  if (!backgroundAudio.hasNext()) return
  backgroundAudio.playNext();
}
</script>

<template>
  <div class="detailed-audio-player">
    <div class="detailed-audio-player__title">{{ title }}</div>
    <div class="detailed-audio-player__controller">
      <AudioController :audio="audio"/>
    </div>
    <div class="detailed-audio-player__button">
      <Icon :class="{locked: !hasPrevious}" :size="24" :color="buttonColor" name="play-next" @click="onPrev"/>
      <div class="detailed-audio-player__button__action" :class="{play: isPlaying}" @click="onClick"></div>
      <Icon :class="{locked: !hasNext}" :size="24" :color="buttonColor" name="play-next" :rotate="180" @click="onNext"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
.locked {
  opacity: 0.3;
  pointer-events: none;
}
.detailed-audio-player {
  &__title {
    font-size: 16px;
    font-weight: 700;
    line-height: 19.34px;
  }

  &__controller {
    padding-top: 18px;
    padding-bottom: 41px;
  }

  &__button {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 40px;
    &__action {
      width: 60px;
      height:60px;
      background-size: contain;
      background-image: url('~@/assets/images/icon/play-2.svg');
      &.play{
        background-image: url('~@/assets/images/icon/pause.svg');
      }
    }
  }
}
</style>
